export type PairedFieldValidationErrors = Record<string, string>;

type PairedFieldValues<ValueKey extends string> = Partial<
  Record<ValueKey, number | null | undefined>
>;

type ValidationResult = {
  isValid: boolean;
  errorString: string;
};

type UpdatePairedFieldValidationErrorsInput<ValueKey extends string> = {
  currentErrors: PairedFieldValidationErrors;
  newValue: string;
  valueKey: ValueKey;
  values: PairedFieldValues<ValueKey>;
  getPairedValueKey: (valueKey: ValueKey) => ValueKey;
  getValidationErrorKey: (valueKey: ValueKey) => string;
  validateValue: (
    newValue: string,
    valueKey: ValueKey,
    comparator: number | null | undefined
  ) => ValidationResult;
};

const formatValidationValue = (value?: number | null) => {
  return value == null ? '' : String(value);
};

export function updatePairedFieldValidationErrors<ValueKey extends string>({
  currentErrors,
  newValue,
  valueKey,
  values,
  getPairedValueKey,
  getValidationErrorKey,
  validateValue,
}: UpdatePairedFieldValidationErrorsInput<ValueKey>): PairedFieldValidationErrors {
  const nextErrors = { ...currentErrors };
  const pairedValueKey = getPairedValueKey(valueKey);
  const comparator = values[pairedValueKey];
  const { isValid, errorString } = validateValue(newValue, valueKey, comparator);
  const currentErrorKey = getValidationErrorKey(valueKey);

  if (isValid) {
    delete nextErrors[currentErrorKey];
  } else {
    nextErrors[currentErrorKey] = errorString;
  }

  const pairedErrorKey = getValidationErrorKey(pairedValueKey);

  if (nextErrors[pairedErrorKey]) {
    const pairedValue = values[pairedValueKey];
    const pairedComparator = values[valueKey];
    const { isValid: isPairedValid, errorString: pairedErrorString } = validateValue(
      formatValidationValue(pairedValue),
      pairedValueKey,
      pairedComparator
    );

    if (isPairedValid) {
      delete nextErrors[pairedErrorKey];
    } else {
      nextErrors[pairedErrorKey] = pairedErrorString;
    }
  }

  return nextErrors;
}
